import * as React from "react";
import PropTypes from "prop-types";
import { Box, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import DisplaySettingsRoundedIcon from "@mui/icons-material/DisplaySettingsRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";

import UserPage from "./UserInfo";
import ChangePassword from "./ChangePassword";
import TwoFactorSettings from "../twoFactor/TwoFactorSettings";

const PremiumTabs = styled(Tabs)(({ theme }) => ({
  width: "100%",
  borderRight: 0,

  "& .MuiTabs-indicator": {
    left: 0,
    width: 4,
    borderRadius: 999,
    background: "linear-gradient(180deg, #1677ff 0%, #22c55e 100%)",
  },

  "& .MuiTabs-flexContainer": {
    gap: theme.spacing(1),
  },
}));

const PremiumTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    minHeight: 58,
    justifyContent: "flex-start",
    alignItems: "center",
    textTransform: "none",
    borderRadius: 16,
    padding: "10px 14px",
    color: "#64748b",
    fontWeight: 800,
    fontSize: 14,
    transition: "all .2s ease",
    border: "1px solid transparent",

    "&:hover": {
      color: "#1677ff",
      background: alpha("#1677ff", 0.07),
    },

    "&.Mui-selected": {
      color: "#1677ff",
      background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
      border: "1px solid #dbeafe",
      boxShadow: "0 10px 26px rgba(15, 23, 42, .06)",
    },

    "&.Mui-focusVisible": {
      backgroundColor: alpha("#1677ff", 0.12),
    },

    [theme.breakpoints.down("md")]: {
      minWidth: 180,
    },
  }),
);

function TabLabel({ icon, title, subtitle }) {
  return (
    <Stack
      direction="row"
      spacing={1.3}
      alignItems="center"
      sx={{ width: "100%" }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 3,
          background: "rgba(255,255,255,.8)",
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "inherit",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ textAlign: "left", minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 14,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: "#94a3b8",
            fontSize: 11,
            fontWeight: 700,
            mt: 0.2,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      sx={{
        width: "100%",
        minWidth: 0,
      }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            width: "100%",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabLabel.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

function a11yProps(index) {
  return {
    id: `account-tab-${index}`,
    "aria-controls": `account-tabpanel-${index}`,
  };
}

export default function UserAccountPage() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const currentTitle =
    value === 0 ? "Personal Information" : "Security Settings";
  const currentSubtitle =
    value === 0
      ? "Manage your profile details and account information."
      : "Update password and protect your account access.";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: "82px",
        px: { xs: 1.5, md: 2.5 },
        pb: 4,
        background:
          "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1420, mx: "auto" }}>
        {/* Page Header */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 5,
            overflow: "hidden",
            mb: 2.2,
            boxShadow: "0 22px 55px rgba(15, 23, 42, .08)",
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#fff",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 4,
                    background: "rgba(255,255,255,.10)",
                    border: "1px solid rgba(255,255,255,.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#93c5fd",
                    boxShadow: "0 14px 34px rgba(0,0,0,.16)",
                  }}
                >
                  <AdminPanelSettingsRoundedIcon sx={{ fontSize: 28 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: { xs: 22, md: 28 },
                      lineHeight: 1.1,
                    }}
                  >
                    Account Settings
                  </Typography>

                  <Typography
                    sx={{
                      color: "#cbd5e1",
                      fontSize: 13,
                      mt: 0.5,
                    }}
                  >
                    Manage your account profile, access and security.
                  </Typography>
                </Box>
              </Stack>

              <Chip
                icon={<VerifiedUserRoundedIcon />}
                label="Secure Account Area"
                sx={{
                  borderRadius: 999,
                  fontWeight: 900,
                  color: "#bfdbfe",
                  background: "rgba(22, 119, 255, .14)",
                  border: "1px solid rgba(147,197,253,.22)",
                  "& .MuiChip-icon": {
                    color: "#93c5fd",
                  },
                }}
              />
            </Stack>
          </Box>
        </Paper>

        {/* Main Layout */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            borderRadius: 5,
            overflow: "hidden",
            minHeight: "calc(100vh - 180px)",
            background: "rgba(255,255,255,.92)",
            boxShadow: "0 22px 55px rgba(15, 23, 42, .08)",
            border: "1px solid rgba(226,232,240,.9)",
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: "100%", md: 300 },
              flexShrink: 0,
              p: 2,
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              borderRight: { xs: 0, md: "1px solid #e5e7eb" },
              borderBottom: { xs: "1px solid #e5e7eb", md: 0 },
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 4,
                background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                border: "1px solid #dbeafe",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 950,
                  color: "#0f172a",
                  fontSize: 16,
                }}
              >
                Settings Menu
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 12,
                  mt: 0.4,
                }}
              >
                Select a section to manage your account.
              </Typography>
            </Box>

            <PremiumTabs
              orientation="vertical"
              variant="scrollable"
              value={value}
              onChange={handleChange}
              aria-label="Account settings tabs"
              sx={{
                display: { xs: "none", md: "flex" },
              }}
            >
              <PremiumTab
                label={
                  <TabLabel
                    icon={<DisplaySettingsRoundedIcon />}
                    title="Personal Info"
                    subtitle="Profile and contact"
                  />
                }
                {...a11yProps(0)}
              />

              <PremiumTab
                label={
                  <TabLabel
                    icon={<LockRoundedIcon />}
                    title="Security"
                    subtitle="Password settings"
                  />
                }
                {...a11yProps(1)}
              />
            </PremiumTabs>

            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={value}
              onChange={handleChange}
              aria-label="Mobile account settings tabs"
              sx={{
                display: { xs: "flex", md: "none" },
                "& .MuiTabs-indicator": {
                  background:
                    "linear-gradient(90deg, #1677ff 0%, #22c55e 100%)",
                  height: 3,
                  borderRadius: 999,
                },
              }}
            >
              <PremiumTab
                label={
                  <TabLabel
                    icon={<DisplaySettingsRoundedIcon />}
                    title="Personal Info"
                    subtitle="Profile and contact"
                  />
                }
                {...a11yProps(0)}
              />

              <PremiumTab
                label={
                  <TabLabel
                    icon={<LockRoundedIcon />}
                    title="Security"
                    subtitle="Password settings"
                  />
                }
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2,
                borderBottom: "1px solid #e5e7eb",
                background: "#fff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      color: "#0f172a",
                      fontSize: 20,
                    }}
                  >
                    {currentTitle}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: 13,
                    }}
                  >
                    {currentSubtitle}
                  </Typography>
                </Box>

                <Chip
                  label={value === 0 ? "Profile" : "Security"}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    fontWeight: 900,
                    color: value === 0 ? "#1d4ed8" : "#15803d",
                    background: value === 0 ? "#eff6ff" : "#f0fdf4",
                  }}
                />
              </Stack>
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 3 },
                background: "#f8fafc",
                minHeight: "calc(100vh - 270px)",
              }}
            >
              <TabPanel value={value} index={0}>
                <UserPage />
              </TabPanel>

              <TabPanel value={value} index={1}>
                <ChangePassword />
                <TwoFactorSettings />
              </TabPanel>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
